import { Inject, Injectable, Injector, Optional } from '@angular/core';
import { HttpClient, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';


import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { ApiErrorModalHelper } from '@shared/helpers/ApiErrorModalHelper';
import { UrlHelper } from '@shared/helpers/UrlHelper';
import { API_BASE_URL, SwaggerException } from './service-proxies';
import { AppConsts } from '@shared/AppConsts';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private baseUrl: string;
    constructor(injector: Injector, private http: HttpClient, private _authService: AppAuthService, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
        this.baseUrl = baseUrl ? baseUrl : "";
    }
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // ✅ Check if app is running as PWA
        const isPwa = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

        // ✅ Check if offline and running as PWA
        if (isPwa && !navigator.onLine) {
            // You can show a custom popup, toast, or modal
            ApiErrorModalHelper.show();

            // Prevent request from going further (optional)
            return of();
        }

        const modifiedRequest = request.clone({
            withCredentials: true
        });

        const isExternalCall = this.baseUrl && !request.url.startsWith(this.baseUrl);

        return next.handle(modifiedRequest).pipe(tap(event => {
            // Only a response from our own API proves the API is reachable. A success against an
            // external host - services.*, analytics, static assets - says nothing about it, and
            // must not clear a genuine "Network Unreachable" state.
            if (!isExternalCall && event instanceof HttpResponse) {
                ApiErrorModalHelper.reportSuccess();
            }
        }), catchError(
            (error: any, caught: Observable<HttpEvent<any>>) => {
                if (isExternalCall || request.headers.has('X-Skip-Interceptor')) {
                    return of();
                }
                else if (request.headers.has('X-Webfacing') && error.status === HttpStatusCode.Unauthorized) {
                    throw error;
                }

                if (error.status === HttpStatusCode.Forbidden) {
                    location.href = AppConsts.appBaseUrl;
                }
                else if (error.status === 401) {
                    this._authService.logout(true);
                    localStorage.setItem("showLogoutMsg", 'true');
                    return of(error);
                }
                else if (error.status == 503 || error.status == 502 || error.status == 504) {
                    if (UrlHelper.isApiStatusUrl(request.url)) {
                        UrlHelper.redirectToMaintenance();
                    }
                }
                else if (error.status === 0) {
                    ApiErrorModalHelper.reportNetworkFailure();
                    return of();
                }
                throw error;
            }
        ));
    }
}

@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        const queryString = location.search; // Returns:'?q=123'

        // Further parsing:
        const params = new URLSearchParams(queryString);

        // Check for "iframe" in the current URL
        let iframeParam = params.get("iframe");

        // If not found, check inside "originalUrl"
        if (!iframeParam) {
            const originalUrlEncoded = params.get("originalUrl"); // Get the originalUrl
            if (originalUrlEncoded) {
                const originalUrl = decodeURIComponent(originalUrlEncoded); // Decode the originalUrl
                const originalParams = new URLSearchParams(originalUrl.split('?')[1]); // Extract query params
                iframeParam = originalParams.get("iframe"); // Look for iframe in originalUrl
            }
        }
        return next.handle(req);
    }
}


export function bypassAbpError<T>() {
    return catchError((err: any) => {
        try {
            const parsed = err instanceof SwaggerException
                ? JSON.parse(err.response)
                : err.error instanceof Blob
                    ? JSON.parse(new TextDecoder().decode(err.error))
                    : err.error;

            if (parsed?.error) {
                return throwError(() => parsed.error);
            }
        } catch {
            return throwError(() => err);
        }

        return throwError(() => err);
    });
}